import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../components/ui/Spinner';
import { PageHeader } from '../components/ui/PageHeader';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';

type PipelineStage =
  | 'planning'
  | 'implementing'
  | 'developer'
  | 'review'
  | 'ready-release'
  | 'pre-release'
  | 'pending-merge'
  | 'post-release'
  | 'completed';

interface PipelineSession {
  id: string;
  title: string;
  projectName?: string;
  stage: PipelineStage;
  stageDetail: string;
  updatedAt: string;
  hermesSessionId?: string;
  daedalusSessionId?: string;
  prometheusSessionId?: string;
  cost?: number;
}

const stageConfig: Record<PipelineStage, { label: string; color: string; bgColor: string }> = {
  planning: { label: 'Planning', color: 'text-primary', bgColor: 'bg-primary-light' },
  implementing: { label: 'Implementing', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  developer: { label: 'Developer', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  review: { label: 'In Review', color: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  'ready-release': { label: 'Ready for Release', color: 'text-teal-600', bgColor: 'bg-teal-50' },
  'pre-release': { label: 'Pre-Release', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  'pending-merge': { label: 'Pending Merge', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  'post-release': { label: 'Post-Release', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  completed: { label: 'Completed', color: 'text-green-600', bgColor: 'bg-green-50' },
};

function SessionRow({ s, onClick }: { s: PipelineSession; onClick: () => void }) {
  const cfg = stageConfig[s.stage];
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4 hover:shadow-md hover:shadow-slate-200/60 transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.bgColor} ${cfg.color} shrink-0`}>
            {cfg.label}
          </span>
          {s.projectName && (
            <span className="text-xs text-gray-500 shrink-0">{s.projectName}</span>
          )}
          <span className="text-sm text-gray-800 font-medium truncate">{s.title}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {s.cost != null && s.cost > 0 && (
            <span className="text-xs text-green-600">${s.cost.toFixed(2)}</span>
          )}
          <span className="text-xs text-gray-500">{new Date(s.updatedAt).toLocaleDateString()}</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-1">{s.stageDetail}</p>
    </button>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { get } = useApi();
  const [sessions, setSessions] = useState<PipelineSession[]>([]);
  const [loading, setLoading] = useState(true);
  const getRef = useRef(get);
  getRef.current = get;

  const fetchPipeline = useCallback(async () => {
    const result = await getRef.current<PipelineSession[]>('/dashboard/pipeline');
    if (result.success && result.data) setSessions(result.data);
    setLoading(false);
  }, []);

  // The dashboard is a live overview — poll it and refresh when the tab regains focus.
  useEffect(() => {
    fetchPipeline();
    const id = setInterval(fetchPipeline, 10000);
    return () => clearInterval(id);
  }, [fetchPipeline]);
  useRefetchOnFocus(fetchPipeline);

  const activeSessions = sessions.filter((s) => s.stage !== 'completed');
  const completedSessions = sessions.filter((s) => s.stage === 'completed').slice(0, 5);

  // Group active sessions by current agent
  const hermesSessions = activeSessions.filter((s) => s.stage === 'planning');
  const daedalusSessions = activeSessions.filter((s) => ['implementing', 'developer', 'review'].includes(s.stage));
  const prometheusSessions = activeSessions.filter((s) => ['ready-release', 'pre-release', 'post-release', 'pending-merge'].includes(s.stage));

  const handleClick = (s: PipelineSession) => {
    if (s.prometheusSessionId) navigate(`/prometheus/${s.prometheusSessionId}`);
    else if (s.daedalusSessionId) navigate(`/daedalus/${s.daedalusSessionId}`);
    else if (s.hermesSessionId) navigate(`/hermes/${s.hermesSessionId}`);
    else navigate('/hermes');
  };

  const agentSections = [
    { key: 'hermes', label: 'Hermes', subtitle: 'Planning', items: hermesSessions, color: 'border-l-primary' },
    { key: 'daedalus', label: 'Daedalus', subtitle: 'Implementing', items: daedalusSessions, color: 'border-l-green-500' },
    { key: 'prometheus', label: 'Prometheus', subtitle: 'Releasing', items: prometheusSessions, color: 'border-l-orange-500' },
  ];

  return (
    <div className="animate-rise">
      <PageHeader
        mark="D"
        markBg="bg-primary-light"
        markText="text-primary"
        title="Dashboard"
        subtitle="Session pipeline across all agents."
      />

      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Active sessions', value: activeSessions.length, accent: 'text-gray-900' },
            { label: 'Planning', value: hermesSessions.length, accent: 'text-primary' },
            { label: 'Implementing', value: daedalusSessions.length, accent: 'text-green-600' },
            { label: 'Releasing', value: prometheusSessions.length, accent: 'text-orange-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4">
              <p className={`text-2xl font-bold tabular-nums ${s.accent}`}>{s.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" className="text-primary" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active sessions grouped by agent */}
          {agentSections.map((section) => (
            <div key={section.key}>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{section.label}</h2>
                <span className="text-xs text-gray-500 italic">{section.subtitle}</span>
                {section.items.length > 0 && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{section.items.length}</span>
                )}
              </div>
              {section.items.length === 0 ? (
                <div className="text-center py-6 text-gray-400 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 text-sm">
                  No active sessions
                </div>
              ) : (
                <div className="space-y-2">
                  {section.items.map((s) => (
                    <SessionRow key={s.id} s={s} onClick={() => handleClick(s)} />
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Completed */}
          {completedSessions.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Recently Completed</h2>
              <div className="space-y-2">
                {completedSessions.map((s) => (
                  <SessionRow key={s.id} s={s} onClick={() => handleClick(s)} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
